import type { FieldMapping } from '@/app/(client)/network/-types';

/** Title-cases an underscore_separated key, e.g. CUT_MAKE_TRIM → "Cut Make Trim" */
export function formatProcess(key: string): string {
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Parses the supply_chain_tier value into an array of tier keys.
 * companies-service returns it as an array of plain string keys, e.g. ["TIER_1","TIER_2"].
 * Also tolerates a JSON/comma string (optimistic local value) and `[{id}]` objects.
 */
export function tierArray(raw: unknown): string[] {
  if (!raw) return [];
  let items: unknown[];
  if (Array.isArray(raw)) {
    items = raw;
  } else if (typeof raw === 'string') {
    if (raw.trimStart().startsWith('[')) {
      try {
        items = JSON.parse(raw) as unknown[];
      } catch {
        items = [];
      }
    } else {
      items = raw.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
    }
  } else {
    items = [];
  }
  return items
    .map(item =>
      typeof item === 'string' ? item : (item as { id?: string })?.id,
    )
    .filter(Boolean) as string[];
}

export function buildFilterParam(filters: Record<string, string[]>): string {
  const out: Record<string, string> = {};
  for (const [key, vals] of Object.entries(filters)) {
    if (vals && vals.length > 0) out[key] = vals.join(',');
  }
  return JSON.stringify(out);
}

export const FIELD_MAP: Record<string, FieldMapping> = {
  facilityProcesses: {
    key: 'facility_processes',
    toApiValue: raw => (raw as string[]).map(id => ({ id })),
    toLocalValue: raw => (raw as string[]).join(','),
  },
  connectionType: {
    key: 'connection_type',
    toApiValue: raw => raw,
    toLocalValue: raw => raw,
  },
  relationshipType: {
    key: 'relationship_type',
    toApiValue: raw => (raw as string[]).map(id => ({ id })),
    toLocalValue: raw => (raw as string[]).map(id => ({ id })),
  },
  responsiblePerson: {
    key: 'responsible_person',
    toApiValue: raw => (raw as string[]).map(id => ({ id })),
    toLocalValue: raw => (raw as string[]).map(id => ({ id })),
  },
  vendors: {
    key: 'vendors',
    toApiValue: raw => (raw as string[]).map(id => ({ id })),
    toLocalValue: raw => (raw as string[]).map(id => ({ id })),
  },
  agents: {
    key: 'agents',
    toApiValue: raw => (raw as string[]).map(id => ({ id })),
    toLocalValue: raw => (raw as string[]).map(id => ({ id })),
  },
  collaborationStatus: {
    key: 'collaboration_status',
    toApiValue: raw => raw,
    toLocalValue: raw => raw,
  },
  supplyChainTier: {
    key: 'supply_chain_tier',
    // Multi editor yields string[] of tier keys; backend stores a plain string array.
    toApiValue: raw => raw,
    toLocalValue: raw => (raw as string[]).join(','),
  },
  collaborationStartDate: {
    key: 'collaboration_start_date',
    toApiValue: raw =>
      raw ? new Date(`${raw}T00:00:00.000Z`).toISOString() : null,
    toLocalValue: raw => raw,
  },
  collaborationEndDate: {
    key: 'collaboration_end_date',
    toApiValue: raw =>
      raw ? new Date(`${raw}T00:00:00.000Z`).toISOString() : null,
    toLocalValue: raw => raw,
  },
  internalCompanyCode: {
    key: 'internal_company_code',
    toApiValue: raw => raw,
    toLocalValue: raw => raw,
  },
  avatarImage: {
    key: 'image_list',
    // The featured image is image_list[0]. Replace only it, preserving the rest,
    // and store the display URL + fileType — matching the profile flow (DELTA-2243).
    toApiValue: (raw, row) => {
      const existing = (
        (row?.imageList ?? []).filter(Boolean) as Array<{
          id: string;
          url: string;
          fileType?: string;
        }>
      ).map(i => ({
        id: i.id,
        url: i.url,
        ...(i.fileType ? { fileType: i.fileType } : {}),
      }));
      // Removal: drop the featured image, keep the rest.
      if (!raw) return existing.slice(1);
      const r = raw as { id: string; url: string; fileType?: string };
      const featured = {
        id: r.id,
        url: r.url,
        ...(r.fileType ? { fileType: r.fileType } : {}),
      };
      return [featured, ...existing.slice(1)];
    },
    toLocalValue: raw => raw,
  },
};
